import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Mic, Play, Calendar, FolderOpen, Download, Zap, Check, ArrowRight, ChevronDown, Sparkles } from 'lucide-react';
import { Pricing } from '../components/ui/pricing';
import { AnimatedLogoRolodex } from '../components/ui/animated-logo-rolodex';
import RadialOrbitalTimeline from '../components/ui/radial-orbital-timeline';
import { renderCanvas } from '../components/ui/canvas';

const Landing = () => {
  const navigate = useNavigate();

  // Initialize canvas animation
  useEffect(() => {
    renderCanvas();
  }, []);

  // Orbital Timeline Data for Features
  const featuresTimelineData = [
    {
      id: 1,
      title: "Voice Logging",
      date: "Core",
      content: "Natural speech recognition with AI processing. Just speak naturally and Logly captures everything.",
      category: "Input",
      icon: Mic,
      relatedIds: [3, 8],
      status: "completed",
      energy: 100,
    },
    {
      id: 2,
      title: "Smart Timers",
      date: "Real-time",
      content: "Track time as it happens with pause/resume functionality. Perfect for interruption-heavy workdays.",
      category: "Tracking",
      icon: Play,
      relatedIds: [1, 4],
      status: "completed",
      energy: 95,
    },
    {
      id: 3,
      title: "AI Intelligence",
      date: "Smart",
      content: "Auto-categorization and project prediction powered by machine learning. Gets smarter over time.",
      category: "AI",
      icon: Sparkles,
      relatedIds: [1, 2, 4],
      status: "in-progress",
      energy: 90,
    },
    {
      id: 4,
      title: "Projects",
      date: "Organize",
      content: "Unlimited color-coded projects with billable tracking. Keep everything organized effortlessly.",
      category: "Organization",
      icon: FolderOpen,
      relatedIds: [2, 3, 5],
      status: "completed",
      energy: 85,
    },
    {
      id: 5,
      title: "Calendar View",
      date: "Visual",
      content: "Beautiful visual time blocks showing your work-life balance. See patterns at a glance.",
      category: "Visualization",
      icon: Calendar,
      relatedIds: [4, 6],
      status: "completed",
      energy: 80,
    },
    {
      id: 6,
      title: "Export Reports",
      date: "Reports",
      content: "CSV and PDF exports for billing and accounting. Professional reports in seconds.",
      category: "Export",
      icon: Download,
      relatedIds: [5, 7],
      status: "completed",
      energy: 75,
    },
    {
      id: 7,
      title: "Shortcuts",
      date: "Speed",
      content: "Keyboard shortcuts for lightning-fast navigation. Power user mode enabled.",
      category: "Productivity",
      icon: Zap,
      relatedIds: [6, 8],
      status: "completed",
      energy: 70,
    },
    {
      id: 8,
      title: "Edit Anytime",
      date: "Flexible",
      content: "Click any entry to modify details instantly. Complete flexibility in how you track.",
      category: "Editing",
      icon: Clock,
      relatedIds: [1, 7],
      status: "completed",
      energy: 65,
    },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const pricingPlans = [
    {
      name: "FREE TRIAL",
      price: "0",
      yearlyPrice: "0",
      period: "7 days",
      showPrice: false,
      features: [
        "All features included",
        "No credit card required",
        "Cancel anytime",
        "Full access to explore",
        "Voice logging with AI",
        "Real-time timers",
        "Unlimited projects",
      ],
      description: "Perfect for trying out Logly",
      billingInfo: "Full access for 7 days",
      buttonText: "Start Free Trial",
      href: "/signup",
      isPopular: false,
    },
    {
      name: "PRO",
      price: "2.99",
      yearlyPrice: "2.39",
      period: "month",
      showPrice: true,
      features: [
        "Unlimited voice entries",
        "Real-time timers",
        "AI categorization & prediction",
        "Unlimited projects",
        "Calendar view",
        "Export CSV & PDF",
        "Keyboard shortcuts",
        "Priority support",
      ],
      description: "Less than a coffee per month",
      billingInfo: "billed monthly",
      yearlyBillingInfo: "billed annually (save 20%)",
      buttonText: "Get Started",
      href: "/signup",
      isPopular: true,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header / Navigation */}
      <header className="fixed top-0 w-full bg-white/90 backdrop-blur-md shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo - Animated */}
            <div className="flex items-center gap-3">
              <AnimatedLogoRolodex size="small" />
              <span className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent" style={{ fontFamily: 'Inter, sans-serif' }}>
                Logly
              </span>
            </div>

            {/* Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('features')} className="text-gray-700 hover:text-cyan-600 font-medium transition-colors">
                Features
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-gray-700 hover:text-cyan-600 font-medium transition-colors">
                Pricing
              </button>
              <button onClick={() => navigate('/docs')} className="text-gray-700 hover:text-cyan-600 font-medium transition-colors">
                Docs
              </button>
              <button onClick={() => navigate('/login')} className="text-gray-700 hover:text-cyan-600 font-medium transition-colors">
                Login
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
              >
                Start Free Trial
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => navigate('/signup')}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-2 rounded-lg font-semibold text-sm"
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-cyan-900 to-slate-900 overflow-hidden">
        {/* Animated Canvas Background */}
        <canvas
          className="absolute inset-0 w-full h-full pointer-events-none"
          id="canvas"
          style={{ opacity: 0.6 }}
        ></canvas>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Stop Losing{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Billable Hours
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-cyan-100 mb-8 leading-relaxed">
              Track your time effortlessly with voice commands and smart timers.<br />
              Just speak - AI does the rest.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <button
                onClick={() => navigate('/signup')}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-2xl flex items-center gap-2 group"
              >
                Start 7-Day Free Trial
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/20 transition-all border-2 border-white/30 flex items-center gap-2"
              >
                See How It Works
                <ChevronDown className="w-5 h-5" />
              </button>
            </div>
            <p className="text-cyan-200 text-sm">
              Then just $2.99/month • No credit card required
            </p>
          </div>

          {/* Feature Icons */}
          <div className="mt-16 flex justify-center items-center gap-8 text-cyan-100">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-400" />
              <span className="text-sm font-medium">Time Tracking</span>
            </div>
            <div className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-pink-400" />
              <span className="text-sm font-medium">Voice Logging</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              <span className="text-sm font-medium">AI Powered</span>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Time Tracking Shouldn't Break Your Flow
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Problems */}
            <div className="space-y-4">
              {[
                'Traditional trackers interrupt your work',
                'Too many clicks, forms, and dropdowns',
                'You forget to log hours and lose money',
                'Manual categorization wastes time'
              ].map((problem, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
                  <span className="text-red-500 text-xl">❌</span>
                  <p className="text-gray-700 font-medium">{problem}</p>
                </div>
              ))}
            </div>

            {/* Solutions */}
            <div className="space-y-4">
              {[
                'Logly: Just speak what you worked on',
                'AI processes and categorizes automatically',
                'Real-time timers for instant tracking',
                'Never forget billable hours again'
              ].map((solution, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                  <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 font-medium">{solution}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Track Time in 3 Simple Steps
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16">
            No complex forms. No tedious clicking. Just natural time tracking.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Mic className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">1. Speak or Start Timer</h3>
              <p className="text-gray-600 leading-relaxed">
                "Worked 2 hours on client meeting with John"<br />
                Or click Quick Start Timer for common projects
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-3xl">🤖</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">2. AI Does the Work</h3>
              <p className="text-gray-600 leading-relaxed">
                Automatically processes speech, fills in details, and suggests projects based on your patterns
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Download className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">3. Review & Export</h3>
              <p className="text-gray-600 leading-relaxed">
                See daily summaries, weekly totals, and export professional timesheets for billing
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/signup')}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-cyan-700 hover:to-blue-700 transition-all shadow-lg inline-flex items-center gap-2"
            >
              Try Voice Entry Now
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Features Section - Orbital Timeline */}
      <section id="features" className="relative">
        <RadialOrbitalTimeline timelineData={featuresTimelineData} />
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-white">
        <Pricing
          plans={pricingPlans}
          title="Simple, Honest Pricing"
          description="Try everything free for 7 days. No credit card required."
        />
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {[
              {
                q: 'How accurate is voice recognition?',
                a: 'Logly uses advanced AI to understand natural speech with high accuracy. The more you use it, the better it gets at understanding your patterns and terminology.'
              },
              {
                q: 'Can I edit entries after creation?',
                a: 'Yes! Click any time entry to edit the project, duration, date, notes, or attendees. Changes are saved automatically.'
              },
              {
                q: 'What happens after my free trial?',
                a: 'After your 7-day trial, you\'ll be asked to subscribe for $2.99/month. You can cancel anytime, and you\'ll retain access until the end of your billing period.'
              },
              {
                q: 'Is my data secure?',
                a: 'Yes! All data is encrypted in transit and at rest. We use industry-standard security practices and never share your data with third parties.'
              },
              {
                q: 'Can I export my data?',
                a: 'Absolutely! Export to CSV or PDF anytime from Settings → Export Data. Your data, your control.'
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.q}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to Stop Losing Billable Hours?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            7-day free trial. Then just $2.99/month.<br />
            No credit card required to start.
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="bg-white text-blue-600 px-12 py-5 rounded-xl font-bold text-xl hover:bg-gray-100 transition-all shadow-2xl inline-flex items-center gap-2"
          >
            Start Your Free Trial Now
            <ArrowRight className="w-6 h-6" />
          </button>
          <p className="mt-8 text-blue-200 text-sm">
            Join 100+ developers tracking time smarter
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <AnimatedLogoRolodex size="small" />
                <span className="text-xl font-bold text-white">Logly</span>
              </div>
              <p className="text-sm">Voice-powered time tracking for modern professionals</p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition-colors">Features</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors">Pricing</button></li>
                <li><button onClick={() => navigate('/docs')} className="hover:text-white transition-colors">Documentation</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/termsofuse')} className="hover:text-white transition-colors">Terms of Service</button></li>
                <li><a href="mailto:support@logly.work" className="hover:text-white transition-colors">Contact Support</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>© 2025 Logly - Voice Time Tracking. Built with ❤️ by @rmax52</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
